import { ChangeDetectionStrategy, Component, inject, Injector, OnInit, Signal } from '@angular/core';
import { BookmarkService } from '../../../@domain/bookmark/application/bookmark.service';
import { PlaylistService } from '../../../@domain/playlist/application/playlist.service';
import { VideoService } from '../../../@domain/video/application/video.service';
import { computedAsync } from '../../../@common/rxjs/computed-async';
import { Page } from '../../../@common/entities/page.interface';
import { PlaylistInfo, SortOrder, VideoInfo } from '../../../@generated';
import { AppRoute } from '../../../routing/types/app-route';
import { SectionHeaderComponent } from '../../../@ui/app/global/section-header/section-header.component';
import { CardGridComponent } from '../../../@ui/app/content/card-grid/card-grid.component';
import { VideoItemComponent } from '../../../@ui/app/content/sections/video-item/video-item.component';
import { PlaylistItemComponent } from '../../../@ui/app/content/sections/playlist-item/playlist-item.component';
import { SpinnerComponent } from '../../../@ui/app/global/spinner/spinner.component';

@Component({
    selector: 'app-profile-dashboard-content',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SectionHeaderComponent,
        CardGridComponent,
        VideoItemComponent,
        PlaylistItemComponent,
        SpinnerComponent,
    ],
    styles: `
        @use "variables" as *;

        .section {
            margin-bottom: 50px;
        }

        .no-content {
            color: $grey-dark;
            font-size: 15px;
            margin-top: 10px;
        }
    `,
    template: `
        <div class="section">
            <app-section-header
                headline="WATCH HISTORY"
                buttonText="WATCH ALL"
                link="/{{ AppRoutes.History }}"/>

            @if (watchHistory(); as history) {
                @if (history.results.length) {
                    <app-card-grid [listView]="false" [maxCards]="5" mobileHorizontalScroll>
                        @for (video of history.results; track video.id; let i = $index) {
                            <app-video-item [video]="video" [part]="video.parts[0]" [priorityImageLoading]="i < 5"/>
                        }
                    </app-card-grid>
                } @else {
                    <p class="no-content">No watch history yet.</p>
                }
            } @else {
                <app-spinner/>
            }
        </div>

        <div class="section">
            <app-section-header
                headline="PLAYLISTS"
                buttonText="VIEW ALL"
                link="/{{ AppRoutes.Profile }}/playlists"/>

            @if (playlists(); as playlists) {
                @if (playlists.length) {
                    <app-card-grid [listView]="false" [maxCards]="5" mobileHorizontalScroll>
                        @for (playlist of playlists.slice(0, 5); track playlist.id) {
                            <app-playlist-item [playlist]="playlist" [listView]="false"/>
                        }
                    </app-card-grid>
                } @else {
                    <p class="no-content">No playlists yet.</p>
                }
            } @else {
                <app-spinner/>
            }
        </div>
    `
})
export class ProfileDashboardComponent implements OnInit {
    private bookmarkService = inject(BookmarkService);
    private playlistService = inject(PlaylistService);
    private videoService = inject(VideoService);
    private injector = inject(Injector);

    public AppRoutes = AppRoute;
    public watchHistory!: Signal<Page<VideoInfo> | undefined>;
    public bookmarks!: Signal<Page<VideoInfo> | undefined>;
    public playlists!: Signal<PlaylistInfo[] | undefined>;

    public ngOnInit() {
        this.watchHistory = computedAsync(
            () => this.videoService.getWatchHistory(1, 5),
            { injector: this.injector }
        );

        this.bookmarks = computedAsync(
            () => this.bookmarkService.getBookmarkedVideos(5, 1, SortOrder.Descending),
            { injector: this.injector }
        );

        this.playlists = computedAsync(
            () => this.playlistService.getUsersPlaylists(),
            { injector: this.injector }
        );
    }
}
